import React, { FC, useRef } from 'react';
import styled from 'styled-components';
import { Colors, Icon, QuestionnaireWithResultSets, useShowAlertPopup, useShowConfirmationPopup } from '../..';
import { FileUpload } from '../general/forms/FileUpload';
import axios from 'axios';

export interface AdditionalQuestionnaireListContentProps {
  item: QuestionnaireWithResultSets;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  justify-content: space-between;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
`;

const DownloadLink = styled.span`
  font-size: 1rem;
  color: ${Colors.primary};
  text-decoration: underline;
  
  :hover {
    cursor: pointer;
    color: ${Colors.primaryLight};
  }
`;

const IconWrapper = styled.div`
  :hover {
    cursor: pointer;
    color: ${Colors.tertiary}
  }
`;

export const AdditionalQuestionnaireListContent: FC<AdditionalQuestionnaireListContentProps> = (props) => {
  const { item } = props;
  const useConfirmationPopup = useShowConfirmationPopup();
  const useNotificationBanner = useShowAlertPopup();
  const fileRef = useRef<QuestionnaireWithResultSets | null>(null);

  return (
    <Wrapper>
      <HeadWrapper>
        <div>Fragebogen definition</div>
        <IconWrapper onClick={() => {
          useConfirmationPopup({
            open: true,
            renderer: () => {
              return <>
                <FileUpload
                  id={'upload-questionnaire'}
                  allowedFileTypes={['application/json', 'text/json']}
                  multiFileUpload={false}
                  label={'Fragebogen definition'}
                  onChange={(files) => {
                    if (files[0]) {
                      const baseFile = files[0].dataUrl.replace('data:application/json;base64,', '') || '';

                      const b64DecodeUtf8 = (str: string) => {
                        return decodeURIComponent(atob(str).split('').map((c) => {
                          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                        }).join(''));
                      };
                      
                      const json = JSON.parse(b64DecodeUtf8(baseFile)) || null;

                      if (json) {
                        fileRef.current = json;
                      }
                    }
                  }} />
              </>;
            },
            actionButtonLabel: 'Speichern',
            closeFn: () => {
              fileRef.current = null;
              useConfirmationPopup(null);
            },
            actionFn: () => {
              if (fileRef.current !== null) {
                const questionnaire = { ...fileRef.current };
                if (questionnaire.id) {
                  axios.put(`/api/questionnaire/${questionnaire.id}`, { ...questionnaire })
                    .then((response) => {
                      useNotificationBanner({
                        renderContent: () => <>Der Fragebogen wurde erfolgreich aktualisiert.</>,
                      });
                      history.go(0);
                    })
                    .catch((e) => {
                      useNotificationBanner({
                        error: true,
                        renderContent: () => <>Bitte prüfen Sie die ausgewählte Datei auf vollstädigkeit und
                          korrektheit.</>,
                      });
                    });
                } else {
                  useNotificationBanner({
                    error: true,
                    renderContent: () => <>Bitte prüfen Sie die ausgewählte Datei auf vollstädigkeit und
                      korrektheit.</>,
                  });
                }

                fileRef.current = null;
                useConfirmationPopup(null);
              } else {
                useNotificationBanner({ error: true, renderContent: () => <>Bitte eine definition auswählen.</> });
                useConfirmationPopup(null);
              }
            },
          });
        }}>
          <Icon icon={'edit'} size={16} />
        </IconWrapper>
      </HeadWrapper>
      <ContentWrapper>
        <div>
          {
            item && <DownloadLink
              onClick={() => {
                const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(item));
                const dlAnchorElem = document.createElement('a');
                dlAnchorElem.setAttribute('href', dataStr);
                dlAnchorElem.setAttribute('download', `${item.questionnaire.type}-definition.json`);
                dlAnchorElem.click();
              }}>Download</DownloadLink>
          }
        </div>
      </ContentWrapper>
    </Wrapper>
  );
};
